/**
 * Converts HTML content to React Email compatible format
 * This utility ensures the HTML generated by the rich text editor
 * works seamlessly with React Email rendering
 */

import type { TemplateVariable } from '@/types/email-template.types';

export function wrapWithReactEmailTemplate(
  htmlContent: string,
  styles: {
    primaryColor: string;
    backgroundColor: string;
    textColor: string;
    headingColor: string;
    fontSize: number;
    fontFamily: string;
  }
): string {
  // Generate inline styles for React Email compatibility
  const containerStyle = `
    max-width: 600px;
    margin: 0 auto;
    font-family: ${styles.fontFamily};
    font-size: ${styles.fontSize}px;
    color: ${styles.textColor};
    background-color: ${styles.backgroundColor};
    padding: 20px;
  `;

  const headingStyle = `
    color: ${styles.headingColor};
    margin-top: 16px;
    margin-bottom: 12px;
  `;

  const linkStyle = `
    color: ${styles.primaryColor};
    text-decoration: none;
  `;

  const buttonStyle = `
    background-color: ${styles.primaryColor};
    color: #ffffff;
    padding: 12px 24px;
    text-decoration: none;
    border-radius: 6px;
    display: inline-block;
    border: none;
    cursor: pointer;
    font-weight: 600;
  `;

  const olStyle = `
    padding-left: 24px;
    margin: 12px 0;
    list-style-type: decimal;
  `;

  const ulStyle = `
    padding-left: 24px;
    margin: 12px 0;
    list-style-type: disc;
  `;

  const liStyle = `
    margin-bottom: 8px;
    padding-left: 4px;
  `;

  // Apply inline styles to elements for better email client compatibility
  let styledContent = htmlContent;

  // Style headings — only add style if the tag does NOT already have a style attribute
  styledContent = styledContent.replace(/<h([1-6])>/g, `<h$1 style="${headingStyle}">`);
  styledContent = styledContent.replace(/<h([1-6])(?![^>]*style\s*=) /g, `<h$1 style="${headingStyle}" `);

  // Style links — only add link style if the <a> tag does NOT already have a style attribute
  // (anchors with existing styles are typically buttons and should keep their original styling)
  styledContent = styledContent.replace(/<a(?![^>]*style\s*=)/g, `<a style="${linkStyle}"`);

  // Style buttons (match both <button> and <button ...>)
  styledContent = styledContent.replace(/<button(?![^>]*style\s*=)(\s|>)/g, `<button style="${buttonStyle}"$1`);

  // Style ordered/unordered lists and list items
  styledContent = styledContent.replace(/<ol(?![^>]*style\s*=)(\s|>)/g, `<ol style="${olStyle}"$1`);
  styledContent = styledContent.replace(/<ul(?![^>]*style\s*=)(\s|>)/g, `<ul style="${ulStyle}"$1`);
  styledContent = styledContent.replace(/<li(?![^>]*style\s*=)(\s|>)/g, `<li style="${liStyle}"$1`);

  // Wrap in React Email compatible structure
  return `
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <style type="text/css">
    body {
      margin: 0;
      padding: 0;
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    table {
      border-collapse: collapse;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
    }
    img {
      border: 0;
      height: auto;
      line-height: 100%;
      outline: none;
      text-decoration: none;
      -ms-interpolation-mode: bicubic;
    }
    ol, ul {
      padding-left: 24px;
    }
    ol {
      list-style-type: decimal;
    }
    ul {
      list-style-type: disc;
    }
    li {
      margin-bottom: 8px;
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: ${styles.backgroundColor};">
  <table border="0" cellpadding="0" cellspacing="0" width="100%">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table border="0" cellpadding="0" cellspacing="0" width="600" style="max-width: 600px;">
          <tr>
            <td style="${containerStyle}">
              ${styledContent}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Extract variables from template content
 */
export function extractVariables(content: string): string[] {
  // Matches {{name}} and {{{name}}} (triple = raw HTML) — both give "name", never "{name".
  const regex = /\{\{\{?\s*([^{}]+?)\s*\}?\}\}/g;
  const variables: string[] = [];
  let match;

  while ((match = regex.exec(content)) !== null) {
    const name = match[1].trim();
    // Skip Handlebars block helpers (#each, /each, #if, /if, else, @index, etc.)
    if (/^[#\/]/.test(name) || name === 'else' || name.startsWith('@')) continue;
    variables.push(name);
  }

  return [...new Set(variables)]; // Remove duplicates
}

/**
 * Keeps every variable the template already allows and adds newly detected ones.
 * Save must never shrink the list, or removed buttons' variables vanish from the panel.
 */
export function mergeAllowedVariables(
  existing: Array<string | TemplateVariable> | undefined,
  detected: string[]
): TemplateVariable[] {
  const byName = new Map<string, TemplateVariable>();
  for (const v of existing || []) {
    const obj: TemplateVariable = typeof v === 'string'
      ? { name: v, type: 'string', description: `Auto-detected variable: ${v}`, required: false }
      : v;
    if (obj?.name && !byName.has(obj.name)) byName.set(obj.name, obj);
  }
  for (const name of detected) {
    if (!byName.has(name)) {
      byName.set(name, { name, type: 'string', description: `Auto-detected variable: ${name}`, required: false });
    }
  }
  return [...byName.values()];
}

// List variables print "[object Object]" as text, so the editor offers their text version instead.
const LIST_VARIABLE_TEXT: Record<string, string> = { badges: 'badge_names' };

export function editorVariableName(name: string): string {
  return LIST_VARIABLE_TEXT[name] || name;
}

// Labels for variables whose name alone is confusing.
const VARIABLE_LABELS: Record<string, string> = {
  badge_plural: "Badge Plural ('s' when 2+ badges, empty for 1)",
};

export function variableLabel(name: string, fallback?: string): string {
  return VARIABLE_LABELS[name] || fallback || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Replace variables with sample or actual data
 */
export function replaceVariables(
  content: string,
  data: Record<string, any>
): string {
  return content.replace(/\{\{\{?\s*([^{}]+?)\s*\}?\}\}/g, (match, varName) => {
    const trimmedVarName = varName.trim();
    // Don't replace Handlebars block helpers (#each, /each, #if, /if, else, @index, etc.)
    if (/^[#\/]/.test(trimmedVarName) || trimmedVarName === 'else' || trimmedVarName.startsWith('@')) {
      return match;
    }
    return data[trimmedVarName] !== undefined
      ? String(data[trimmedVarName])
      : match;
  });
}
